import { Box, Typography } from "@mui/material";

import { Error, Info, TokenOutlined } from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import DeleteIcon from "../../../../assets/images/icons/DeleteIcon";
import { UploadIcon } from "../../../../assets/images/icons/UploadIcon";
import BriaIconButton from "../../../../components/common/BriaIconButton/BriaIconButton";
import ImageUploader from "../../../../components/common/ImageUploader/ImageUploader";
import StoppableButton from "../../../../components/common/StoppableButton/StoppableButton";
import { useAppStore } from "../../../../hooks/useStores";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { VideoValidationErrors, useVideoEditingConfig } from "../hooks/useVideoEditingConfig";
import APIPayloadPreview from "./APIPayloadPreview/APIPayloadPreview";
import VideoEditingToolsDropdown from "./ToolsDropdown/VideoEditingToolsDropdown";
import styles from "./VideoEditingConfig.module.scss";

const VideoEditingConfig = () => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab" });
	const { videoEditingStore, videoGenerationStore, authStore, anonymousUsageStore } = useAppStore();
	const { validateConfig, getVideoMetadata, validateVideoConstraints } = useVideoEditingConfig();
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);
	const isAnonymous = !authStore.isLoggedIn;
	const activeTool = videoEditingStore.config.tool || "erase";
	const videoFeature = `labs_video_${activeTool}`;
	const videoUsage = anonymousUsageStore.getUsage(videoFeature);

	const isButtonDisabled = !validateConfig(videoEditingStore.config);

	const handleEditVideo = async () => {
		if (isButtonDisabled) {
			return;
		}

		await videoEditingStore.editVideo();
	};

	const handleUploadVideo = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		try {
			setLoading(true);

			videoEditingStore.clearErrors();
			videoGenerationStore.clearSegmentConfig();

			const [videoUrl, metadata] = await Promise.all([
				uploadFileToS3(file, "video", setLoadingProgress),
				getVideoMetadata(file),
			]);

			videoEditingStore.handleConfigChange("video", videoUrl);
			videoEditingStore.validateAllTools(videoUrl, metadata, validateVideoConstraints);
		} catch (error: any) {
			const errorMessage = error?.message || error?.toString();
			videoEditingStore.errors.upload = {
				message: errorMessage,
				status: error?.status || 500,
			};
		} finally {
			setLoadingProgress(undefined);
			setLoading(false);
		}
	};

	const handleDeleteVideo = async () => {
		videoEditingStore.clearErrors();
		videoEditingStore.handleConfigChange("video", "");
	};

	const errorMessages: Array<{ message: string; isWarning: boolean }> = [];
	const selectedTool = videoEditingStore.config.tool;

	Object.entries(videoEditingStore.errors).forEach(([key, error]) => {
		if (!error) return;

		const isGeneralError = key === "editVideo" || key === "upload";
		const isSelectedToolError = selectedTool && key === selectedTool;

		if (!isGeneralError && !isSelectedToolError) return;

		if ("message" in error) {
			errorMessages.push({ message: error.message, isWarning: false });
		} else {
			const validationError = error as VideoValidationErrors;
			if (validationError.fps) errorMessages.push({ message: validationError.fps, isWarning: false });
			if (validationError.duration) errorMessages.push({ message: validationError.duration, isWarning: true });
		}
	});

	return (
		<Box className={styles.container}>
			<ImageUploader
				src={videoEditingStore.config.video}
				className={styles.videoUploader}
				emptyStateClassName={styles.videoUploaderEmptyState}
				inputProps={{ accept: "video/*" }}
				dropOverlayProps={{
					icon: <UploadIcon />,
					text: t("uploader.dropOverlayText"),
				}}
				description={
					<Box className={styles.videoUploaderDescriptionContainer}>
						<Typography className={styles.videoUploaderTitle}>{t("uploader.title")}</Typography>
						<Typography className={styles.videoUploaderDescription}>{t("uploader.description")}</Typography>
					</Box>
				}
				icon={<UploadIcon size={10} />}
				showIcon
				smallIcon
				hideSupportElement
				showTitle
				itemDescription={t("uploader.itemDescription")}
				customOverlay={({ handleUploadClick, handleDelete }) => (
					<>
						<BriaIconButton
							square
							className={styles.customOverlayButton}
							onClick={handleUploadClick}
							title={t("upload")}
						>
							{<UploadIcon />}
						</BriaIconButton>
						<BriaIconButton
							square
							className={styles.customOverlayButton}
							onClick={handleDelete}
							title={t("delete")}
						>
							<DeleteIcon />
						</BriaIconButton>
					</>
				)}
				customOverlayClassName={styles.customOverlay}
				onUpload={handleUploadVideo}
				onDelete={handleDeleteVideo}
				loading={loading}
				loadingProgress={loadingProgress}
			/>

			{errorMessages.map(({ message, isWarning }, index) => (
				<Box key={index} className={isWarning ? styles.warningMessageContainer : styles.errorMessageContainer}>
					{isWarning ? <Info /> : <Error />}
					<Trans>
						<Typography className={isWarning ? styles.warningMessage : styles.errorMessage}>
							{message}
						</Typography>
					</Trans>
				</Box>
			))}
			<VideoEditingToolsDropdown />

			<APIPayloadPreview />

			<StoppableButton
				condition={videoEditingStore.loadingEdit}
				onStopButtonClick={() => videoEditingStore.abortVideoEdit()}
				onButtonClick={handleEditVideo}
				buttonLabel={
					<>
						{t("actions.editVideo")}
						{isAnonymous && (
							<span
								style={{
									marginLeft: 8,
									display: "inline-flex",
									alignItems: "center",
									gap: 4,
									fontSize: "0.85em",
									opacity: 0.9,
								}}
							>
								{videoUsage.used}/{videoUsage.limit}
								<TokenOutlined style={{ fontSize: "1.1em" }} />
							</span>
						)}
					</>
				}
				stopButtonLabel={t("actions.stopEditVideo")}
				disabled={isButtonDisabled}
				className={styles.actionButton}
			/>
		</Box>
	);
};

export default observer(VideoEditingConfig);
